import { getThemeHeadingLevel } from '@adornis/buchungsheld-db/legacy/migrate-legacy-buildify-html.js';
import { UndoMenuItem } from '@adornis/prosemirror-editor/client/menu-items/UndoMenuItem.js';
import { HEADING_SCHEMA } from '@adornis/prosemirror-editor/nodes/headings/schema.js';
import { PARAGRAPH_SCHEMA } from '@adornis/prosemirror-editor/nodes/paragraph/schema.js';
import type { EditorConfig } from '@adornis/prosemirror-editor/types.js';
import { flexNodeConfig } from './nodes/flex/config.js';

/** Keep stored rich text readable without modifying the caller's preset. */
export function withLegacyCompat<T extends readonly EditorConfig[]>(configs: T): T {
  const headingSchema = HEADING_SCHEMA();
  const headingRules = headingSchema.parseDOM ?? [];
  return configs.map<EditorConfig>(config => {
    if (config.menuItems?.includes(UndoMenuItem)) {
      config = {
        ...config,
        menuItems: config.menuItems.map(item => (item === UndoMenuItem ? { ...item, label: 'Rückgängig machen' } : item)),
      };
    }
    if ('node' in config) {
      if (config.node.name === 'flex') return flexNodeConfig;
      if (config.node.name === 'paragraph') {
        return { ...config, node: { ...config.node, schema: PARAGRAPH_SCHEMA({ tag: 'tr-text' }) } };
      }
      if (config.node.name === 'heading') {
        const legacyRules = headingRules.flatMap(rule =>
          'tag' in rule && /^h[1-6]$/.test(rule.tag ?? '') ? [{ ...rule, tag: `tr-${rule.tag}` }] : [],
        );
        return {
          ...config,
          node: {
            ...config.node,
            schema: {
              ...config.node.schema,
              parseDOM: [...(config.node.schema.parseDOM ?? []), ...legacyRules],
              toDOM: node => {
                // Retain the engine's anchor/numbering metadata on the theme element.
                const spec = headingSchema.toDOM!(node);
                const attrs = { ...(Array.isArray(spec) ? spec[1] : {}) };
                if (node.attrs.textAlign && node.attrs.textAlign !== 'left') {
                  attrs.style = `text-align: ${node.attrs.textAlign};`;
                }
                return [`tr-h${getThemeHeadingLevel(node.attrs.level)}`, attrs, 0];
              },
            },
          },
        };
      }
    }
    if ('mark' in config && config.mark.name === 'link') {
      return {
        ...config,
        mark: {
          ...config.mark,
          schema: {
            ...config.mark.schema,
            parseDOM: [
              ...(config.mark.schema.parseDOM ?? []),
              {
                tag: 'tr-link',
                getAttrs: (dom: HTMLElement) => ({
                  href: dom.getAttribute('href'),
                  target: dom.getAttribute('target') ?? '_blank',
                }),
              },
            ],
          },
        },
      };
    }
    return config;
  }) as T & EditorConfig[];
}
